'use client';

import { ImgHTMLAttributes, ReactNode, useEffect, useMemo, useState } from 'react';
import { buildApexLegendIconFallbackUrl, buildApexLegendIconUrl } from '@/lib/apex';

interface ApexLegendIconProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  /** Caller-provided URL (e.g. apex_profiles.selected_legend_icon). Tried first. */
  url?: string | null;
  /** Legend name. Used to derive backup URLs from known CDNs. */
  legend?: string | null;
  /** Rendered if every source fails. */
  placeholder?: ReactNode;
}

// Walks a multi-source fallback chain so a 404 on one CDN drops to the next:
//   1. caller-provided URL (e.g. Mozambique URL stored on the profile)
//   2. apexlegendsstatus.com humanesas (covers every legend through S29 + local override for Mad Maggie)
//   3. Mozambique icons (covers older legends if humanesas is down)
export function ApexLegendIcon({
  url,
  legend,
  placeholder = null,
  alt,
  ...rest
}: ApexLegendIconProps) {
  const sources = useMemo(() => {
    const list: string[] = [];
    if (url) list.push(url);
    const primary = buildApexLegendIconUrl(legend);
    if (primary && !list.includes(primary)) list.push(primary);
    const fallback = buildApexLegendIconFallbackUrl(legend);
    if (fallback && !list.includes(fallback)) list.push(fallback);
    return list;
  }, [url, legend]);

  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [sources]);

  if (idx >= sources.length) return <>{placeholder}</>;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      {...rest}
      src={sources[idx]}
      alt={alt ?? legend ?? 'Apex Legend'}
      onError={() => setIdx(idx + 1)}
    />
  );
}
